import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { formatApiError, useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, PlusCircle, Waves, TrendingUp, AlertTriangle, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const [items, setItems] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/diagnostic/list")
      .then(({ data }) => setItems(data.items))
      .catch((err) => toast.error(formatApiError(err)));
  }, []);

  if (!items) {
    return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  const last = items[0];
  const totalIssues = items.reduce((acc, d) => acc + (d.result?.total_issues || 0), 0);
  const criticalCount = items.reduce((acc, d) => acc + (d.result?.issues?.filter?.((i) => i.severity === "critical")?.length || 0), 0);
  const avgScore = items.length > 0 ? Math.round(items.reduce((a, d) => a + (d.result?.overall_score || 0), 0) / items.length) : 0;

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-12" data-testid="dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow">Tableau de bord</div>
          <h1 className="mt-2 font-display text-3xl lg:text-4xl font-bold tracking-tight">
            Bonjour {user?.name?.split(" ")[0] || "à vous"}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">Suivi de l'état de votre spa dans le temps.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/diagnostic">
            <Button className="rounded-full px-6 h-11" data-testid="dashboard-new-diagnostic">
              <PlusCircle className="h-4 w-4 mr-2" /> Nouveau diagnostic
            </Button>
          </Link>
        </div>
      </div>

      {/* KPIs */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <Kpi label="Diagnostics effectués" value={items.length} icon={Waves} accent="text-primary bg-primary/10" testid="kpi-total" />
        <Kpi label="Score moyen" value={`${avgScore}/100`} icon={TrendingUp} accent="text-emerald-600 bg-emerald-100" testid="kpi-avg" />
        <Kpi label="Problèmes identifiés" value={totalIssues} icon={AlertTriangle} accent="text-amber-600 bg-amber-100" testid="kpi-issues" />
        <Kpi label="Abonnement" value={user?.subscription_status === "active" ? "Premium" : "Gratuit"} icon={Sparkles} accent="text-violet-600 bg-violet-100" testid="kpi-plan" />
      </div>

      {/* Last diagnostic */}
      <div className="mt-12">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-semibold">Diagnostics récents</h2>
          <Link to="/history" className="text-sm text-primary hover:underline">Tout l'historique →</Link>
        </div>

        {items.length === 0 ? (
          <Card className="p-12 border-dashed border-black/15 text-center" data-testid="empty-state">
            <Waves className="h-10 w-10 mx-auto text-muted-foreground" />
            <h3 className="mt-4 font-display text-xl font-semibold">Aucun diagnostic pour l'instant</h3>
            <p className="mt-2 text-sm text-muted-foreground">Lancez votre premier diagnostic gratuit en 2 minutes.</p>
            <Link to="/diagnostic"><Button className="mt-6 rounded-full">Démarrer</Button></Link>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.slice(0, 6).map((d) => <DiagnosticRow key={d.id} d={d} />)}
          </div>
        )}
      </div>

      {/* Upgrade banner */}
      {user?.subscription_status !== "active" && (
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="rounded-2xl bg-secondary text-white p-8 flex flex-col justify-between gap-6">
            <div>
              <div className="eyebrow text-white/60">Formule Premium</div>
              <h3 className="mt-2 font-display text-2xl font-bold">Diagnostics illimités + tous les rapports.</h3>
              <p className="mt-1 text-sm text-white/70">14,99€/mois — sans engagement.</p>
            </div>
            <Link to="/pricing">
              <Button className="bg-white text-secondary hover:bg-white/90 rounded-full h-11 px-6 w-full sm:w-auto" data-testid="upgrade-cta">
                Découvrir Premium
              </Button>
            </Link>
          </div>
          <div className="rounded-2xl border-2 border-dashed border-primary/30 bg-primary/5 p-8 flex flex-col justify-between gap-6">
            <div>
              <div className="eyebrow text-primary">Parrainage</div>
              <h3 className="mt-2 font-display text-2xl font-bold">Parrainez, gagnez du Premium.</h3>
              <p className="mt-1 text-sm text-muted-foreground">1 ami parrainé = 30 jours Premium offerts. Illimité.</p>
            </div>
            <Link to="/referrals">
              <Button variant="outline" className="rounded-full h-11 px-6 w-full sm:w-auto" data-testid="dashboard-referrals-cta">
                Mon code de parrainage
              </Button>
            </Link>
          </div>
        </div>
      )}
      {user?.subscription_status === "active" && (
        <div className="mt-12 rounded-2xl border-2 border-dashed border-primary/30 bg-primary/5 p-8 flex flex-col md:flex-row md:items-center gap-6 justify-between">
          <div>
            <div className="eyebrow text-primary">Parrainage</div>
            <h3 className="mt-2 font-display text-2xl font-bold">Aidez un ami à diagnostiquer son spa.</h3>
            <p className="mt-1 text-sm text-muted-foreground">1 ami parrainé = 30 jours Premium supplémentaires offerts.</p>
          </div>
          <Link to="/referrals">
            <Button className="rounded-full h-11 px-6" data-testid="dashboard-referrals-cta">Mon code de parrainage</Button>
          </Link>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, icon: Icon, accent, testid }) {
  return (
    <Card className="p-6 border-black/10" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="eyebrow">{label}</div>
        <span className={`h-8 w-8 grid place-items-center rounded-md ${accent}`}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <div className="mt-3 font-display text-3xl font-black">{value}</div>
    </Card>
  );
}

function DiagnosticRow({ d }) {
  const score = d.result?.overall_score ?? 0;
  const scoreColor = score >= 75 ? "text-emerald-600" : score >= 50 ? "text-amber-600" : "text-red-600";
  const date = d.created_at ? new Date(d.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }) : "";
  return (
    <Link to={`/results/${d.id}`} className="block group" data-testid={`diagnostic-item-${d.id}`}>
      <Card className="p-5 border-black/10 h-full hover:-translate-y-1 hover:shadow-md transition-transform">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs text-muted-foreground">{date}</div>
            <div className="mt-1 font-display text-lg font-semibold group-hover:text-primary transition-colors">
              {d.result?.headline || "Diagnostic"}
            </div>
          </div>
          <div className={`font-display text-3xl font-black tabular-nums ${scoreColor}`}>{score}</div>
        </div>
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          {d.paid ? (
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Rapport débloqué</Badge>
          ) : (
            <Badge variant="outline">Aperçu</Badge>
          )}
          {(d.result?.total_issues ?? 0) > 0 && (
            <Badge variant="outline">{d.result.total_issues} point{d.result.total_issues > 1 ? "s" : ""}</Badge>
          )}
        </div>
      </Card>
    </Link>
  );
}
